"use client";

import { useCallback, useEffect, useRef } from "react";

export interface CoalescedRefresh {
  /** Request a refresh "soon". Bursts within the delay window collapse into one trailing run. */
  schedule: () => void;
  /**
   * Run a refresh NOW, cancelling any pending timer. Resolves only once server
   * state at least as fresh as every prior schedule()/flush() has been fetched:
   * if a refresh is already in flight, flush waits for it AND for one trailing
   * refresh that starts after it completes. Rejects if that refresh rejects.
   */
  flush: () => Promise<void>;
  /**
   * Drop the pending timer and any queued trailing run (in-flight fetches are
   * not aborted; their results still land). Use on unmount or when the target
   * of the refresh changes (e.g. navigating to a different document).
   * Any flush() promises still pending at cancel time settle immediately,
   * WITHOUT the freshness guarantee — they belong to the abandoned document.
   */
  cancel: () => void;
}

/**
 * Coalesces bursts of refresh requests into a single trailing call, and
 * guarantees at most one refresh is in flight at a time.
 *
 * Error semantics: timer-fired (scheduled) refresh failures are swallowed
 * (logged) — optimistic local state stands and the next refresh self-heals.
 * flush() callers get the rejection.
 */
export function createCoalescedRefresh(
  refreshFn: () => Promise<void>,
  delayMs = 400
): CoalescedRefresh {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight: Promise<void> | null = null;
  let trailing: Promise<void> | null = null;

  const run = (): Promise<void> => {
    if (inFlight) {
      // A refresh is flying — queue exactly one trailing run after it settles.
      if (!trailing) {
        const t: Promise<void> = inFlight
          .catch(() => {}) // the in-flight owner handles its own rejection
          .then(() => {
            if (trailing !== t) return; // cancel() disowned this queued run
            trailing = null;
            return run();
          });
        trailing = t;
      }
      return trailing;
    }
    const p = Promise.resolve()
      .then(() => refreshFn())
      .finally(() => {
        if (inFlight === p) inFlight = null;
      });
    inFlight = p;
    return p;
  };

  const schedule = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      run().catch((err) => {
        console.error("Coalesced refresh failed:", err);
      });
    }, delayMs);
  };

  const flush = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    return run();
  };

  const cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    // Disown the queued trailing run (its handler sees trailing !== itself and
    // no-ops) and free the slot, so any run()/flush() issued AFTER this cancel
    // queues a FRESH trailing chained on the still-in-flight fetch, if any.
    trailing = null;
  };

  return { schedule, flush, cancel };
}

/**
 * Hook wrapper around {@link createCoalescedRefresh}. Rapid field edits each
 * request a refresh; only one fetch flies, plus at most one trailing fetch.
 *
 * Always invokes the LATEST `refreshFn` identity (no stale closures).
 * `delayMs` is fixed at first render. The pending timer is cancelled on
 * unmount; call `cancel()` yourself when the refresh target changes without
 * an unmount (e.g. /documents/[id] navigation where the page persists).
 */
export function useCoalescedRefresh(
  refreshFn: () => Promise<void>,
  delayMs = 400
): CoalescedRefresh {
  const fnRef = useRef(refreshFn);
  fnRef.current = refreshFn;

  const controllerRef = useRef<CoalescedRefresh | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = createCoalescedRefresh(() => fnRef.current(), delayMs);
  }

  useEffect(() => {
    const controller = controllerRef.current;
    return () => controller?.cancel();
  }, []);

  const schedule = useCallback(() => controllerRef.current!.schedule(), []);
  const flush = useCallback(() => controllerRef.current!.flush(), []);
  const cancel = useCallback(() => controllerRef.current!.cancel(), []);

  return { schedule, flush, cancel };
}
